import React, { useMemo, useRef, useState } from "react";
import { Wrapper } from "./style";
import { Helmet } from "react-helmet";
import { Button, ConfigProvider, Table, Tag } from "antd";
import {
  CheckCircleFilled,
  LeftOutlined,
  RightOutlined,
  DownloadOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";

const Billing = () => {
  const [billingPeriod, setBillingPeriod] = useState("monthly");
  const plansScrollRef = useRef(null);

  const renewalDate = useMemo(() => dayjs().add(22, "day"), []);

  const currentPlan = useMemo(
    () => ({
      name: "Growth",
      priceMonthly: 97,
      description: "Dashboards + alerts + recommended fixes.",
    }),
    []
  );

  const availablePlans = useMemo(
    () => [
      {
        key: "free",
        name: "Free",
        priceMonthly: 0,
        perks: ["Basic P&L snapshot", "Limited leak highlights", "Community support"],
        cta: "Choose Free",
        highlighted: false,
      },
      {
        key: "starter",
        name: "Starter",
        priceMonthly: 29,
        perks: ["Core dashboards (P&L)", "Weekly health report", "Email support"],
        cta: "Choose Starter",
        highlighted: true,
      },
      {
        key: "growth",
        name: "Growth",
        priceMonthly: 97,
        perks: ["All Starter features", "Alerts + recommendations", "Priority support"],
        cta: "Choose Growth",
        highlighted: false,
      },
      {
        key: "pro",
        name: "Pro",
        priceMonthly: 199,
        perks: ["Multi-store reporting", "Advanced automations", "Dedicated onboarding"],
        cta: "Contact sales",
        highlighted: false,
      },
    ],
    []
  );

  const invoices = useMemo(
    () => [
      { id: "inv_2026_02_15", date: "2026-02-15", plan: "Growth · Monthly", amount: 97 },
      { id: "inv_2026_01_15", date: "2026-01-15", plan: "Growth · Monthly", amount: 97 },
      { id: "inv_2025_12_15", date: "2025-12-15", plan: "Starter · Monthly", amount: 29 },
    ],
    []
  );

  const paymentMethods = useMemo(
    () => [
      { id: "pm_visa_4057", brand: "Visa", last4: "4057", name: "ADSIFY TECHNOLOGIES", isDefault: true },
      { id: "pm_mc_1122", brand: "Mastercard", last4: "1122", name: "ADSIFY TECHNOLOGIES", isDefault: false },
    ],
    []
  );

  const invoiceColumns = useMemo(
    () => [
      {
        title: "Date",
        dataIndex: "date",
        key: "date",
        render: (d) => dayjs(d).format("YYYY-MM-DD"),
      },
      {
        title: "Plan",
        dataIndex: "plan",
        key: "plan",
        render: (p) => <Tag className="billing-tag">{p}</Tag>,
      },
      {
        title: "Amount",
        dataIndex: "amount",
        key: "amount",
        render: (a) => <span className="billing-amount">${a}</span>,
      },
      {
        title: "Invoice",
        key: "invoice",
        render: (_, row) => (
          <Button type="link" className="billing-link" icon={<DownloadOutlined />}>
            Download
          </Button>
        ),
      },
    ],
    []
  );

  const scrollPlans = (direction) => {
    const el = plansScrollRef.current;
    if (!el) return;
    const delta = direction === "left" ? -420 : 420;
    el.scrollBy({ left: delta, behavior: "smooth" });
  };

  const priceFor = (p) => {
    if (billingPeriod === "monthly") return p.priceMonthly;
    return Math.round(p.priceMonthly * 10);
  };

  return (
    <Wrapper>
      <Helmet>
        <title>Billing</title>
      </Helmet>

      <div className="billing-header ">
        <div className="billing-header__left">
          <div className="billing-title">Billing</div>
          <div className="billing-subtitle">Manage your plan, download invoices, and update payment methods.</div>
        </div>
        <div className="billing-header__right">
          <div className="billing-status">
            <CheckCircleFilled className="billing-status__icon" />
            <span className="billing-status__text">
              Active <span className="billing-status__dot">•</span> Next renewal:{" "}
              <b>{renewalDate.format("MMM D, YYYY")}</b>
            </span>
          </div>
        </div>
      </div>

      {/* Plans */}
      <div className="row gy-5 g-xl-5">
        <div className="col-xxl-12">
          <div className="card card-xxl-stretch mb-5 mb-xl-8 billing-card">
            <div className="card-header border-bottom-dashed billing-card__header">
              <div>
                <div className="billing-section-title">Plans</div>
                <div className="billing-section-subtitle">Upgrade or downgrade anytime.</div>
              </div>
              <div className="billing-period">
                <div className="billing-period__label">Billing:</div>
                <div className="billing-period__toggle">
                  <button
                    className={`billing-pill ${billingPeriod === "monthly" ? "is-active" : ""}`}
                    onClick={() => setBillingPeriod("monthly")}
                    type="button"
                  >
                    Monthly
                  </button>
                  <button
                    className={`billing-pill ${billingPeriod === "yearly" ? "is-active" : ""}`}
                    onClick={() => setBillingPeriod("yearly")}
                    type="button"
                  >
                    Yearly
                  </button>
                </div>
              </div>
            </div>

            <div className="card-body pt-6">
              <div className="billing-plans-grid row gy-5">
                <div className="col-lg-3">
                <div className="billing-current  ">
                  <div className="billing-current__badge">CURRENT PLAN</div>
                  <div className="billing-current__name">{currentPlan.name}</div>
                  <div className="billing-current__price">
                    ${currentPlan.priceMonthly} <span>/ month</span>
                  </div>
                  <div className="billing-current__desc">{currentPlan.description}</div>

                  <div className="billing-current__actions">
                    <Button className="billing-btn" type="primary" danger>
                      Upgrade
                    </Button>
                    <Button className="billing-btn" danger ghost>
                      Downgrade
                    </Button>
                  </div>

                  <div className="billing-hint">
                    You can switch plans anytime. If you downgrade, features update based on your new plan.
                  </div>
                </div>
                </div>
                <div className="col-lg-9">
                <div className="billing-available">
                  <div className="billing-available__top">
                    <div className="billing-available__title">Available plans (scroll)</div>
                    <div className="billing-available__nav">
                      <Button
                        className="billing-icon-btn"
                        icon={<LeftOutlined />}
                        onClick={() => scrollPlans("left")}
                      />
                      <Button
                        className="billing-icon-btn"
                        icon={<RightOutlined />}
                        onClick={() => scrollPlans("right")}
                      />
                    </div>
                  </div>

                  <div className="billing-scroll" ref={plansScrollRef}>
                    {availablePlans.map((p) => (
                      <div
                        key={p.key}
                        className={`billing-plan ${p.highlighted ? "is-highlighted" : ""}`}
                      >
                        <div className="billing-plan__name">{p.name}</div>
                        <div className="billing-plan__price">
                          <span className="billing-plan__priceValue">${priceFor(p)}</span>
                          <span className="billing-plan__priceUnit">/ {billingPeriod === "monthly" ? "month" : "year"}</span>
                        </div>
                        <ul className="billing-plan__perks">
                          {p.perks.map((perk) => (
                            <li key={perk}>
                              <span className="billing-check" />
                              {perk}
                            </li>
                          ))}
                        </ul>
                        <Button className="billing-plan__cta" type={p.highlighted ? "primary" : "default"} danger>
                          {p.cta}
                        </Button>
                      </div>
                    ))}
                  </div>

                  <div className="billing-scrollbar-hint" />
                </div>
                </div>
                
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Invoices */}
      <div className="row gy-5 g-xl-5">
        <div className="col-xxl-12">
          <div className="card card-xxl-stretch mb-5 mb-xl-8 billing-card">
            <div className="card-header border-bottom-dashed billing-card__header">
              <div>
                <div className="billing-section-title">Invoices</div>
                <div className="billing-section-subtitle">Download previous invoices for your records.</div>
              </div>
              <Button className="billing-btn" type="default" variant="outlined" icon={<DownloadOutlined />}>
                Download all
              </Button>
            </div>
            <div className="card-body pt-2">
              <ConfigProvider theme={{ token: { colorPrimary: "#f45b68" } }}>
                <div className="table-responsive">
                  <Table
                    columns={invoiceColumns}
                    dataSource={invoices}
                    rowKey="id"
                    pagination={false}
                    components={{
                      table: (p) => (
                        <table className="table table-row-dashed table-row-gray-300 align-middle gs-4 gy-4 fs-7" {...p} />
                      ),
                      header: { row: (p) => <tr {...p} className="fw-bolder text-dark bg-cus" /> },
                    }}
                  />
                </div>
              </ConfigProvider>
            </div>
          </div>
        </div>
      </div>

      {/* Payment methods */}
      <div className="row gy-5 g-xl-5">
        <div className="col-xxl-12">
          <div className="card card-xxl-stretch mb-5 mb-xl-8 billing-card">
            <div className="card-header border-bottom-dashed billing-card__header">
              <div>
                <div className="billing-section-title">Payment methods</div>
                <div className="billing-section-subtitle">Manage cards used for workspace billing.</div>
              </div>
              <Button className="billing-btn" icon={<PlusOutlined />}>
                Add payment method
              </Button>
            </div>

            <div className="card-body pt-6">
              <div className="billing-payments-grid">
                <div className="billing-payments">
                  {paymentMethods.map((pm) => (
                    <div key={pm.id} className={`mb-4 billing-payCard ${pm.isDefault ? "is-default" : ""}`}>
                      <div className="billing-payCard__left">
                        <div className="billing-payCard__brand">{pm.brand}</div>
                        <div className="billing-payCard__meta">
                          <div className="billing-payCard__number">
                            {pm.brand} ending •••• {pm.last4}
                            {pm.isDefault && <span className="billing-defaultTag">Default</span>}
                          </div>
                          <div className="billing-payCard__name">Name on card: {pm.name}</div>
                        </div>
                      </div>
                      <div className="billing-payCard__actions">
                        <Button className="billing-btn" disabled={pm.isDefault}>
                          Set default
                        </Button>
                        <Button className="billing-btn" danger ghost>
                          Remove
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="billing-billingInfo">
                  <div className="billing-billingInfo__title">Billing info</div>
                  <div className="billing-billingInfo__row">
                    <div className="billing-billingInfo__label">Workspace:</div>
                    <div className="billing-billingInfo__value">Sellerview</div>
                  </div>
                  <div className="billing-billingInfo__row">
                    <div className="billing-billingInfo__label">Billing email:</div>
                    <div className="billing-billingInfo__value">billing@sellerview.ai</div>
                  </div>
                  <div className="billing-billingInfo__row">
                    <div className="billing-billingInfo__label">Currency:</div>
                    <div className="billing-billingInfo__value">USD</div>
                  </div>
                  <Button className="billing-btn w-100" danger ghost>
                    Update billing info
                  </Button>
                  <div className="billing-hint mt-3">Keep billing email updated to receive invoices and payment alerts.</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Wrapper>
  );
};

export default Billing;